import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { audioUrl } from "./GermanNounArticles/audio";
import GameOver from "./ArticleRunner/GameOver";
import Leaderboard from "./ArticleRunner/Leaderboard";
import {
  GERMAN_ARTICLES, LANE_COLORS, PIXEL_FONT, burst, withArticle, drawArticles, drawBackdrop, drawGate,
  drawParticles, drawRunner, drawSpeedLines, dust, makeView,
} from "./ArticleRunner/render";
import {
  LIVES, hud as hudState, idleGame, moveTo as moveRunner, newGame, pointsFor, spawnGate,
  startDash, step as stepGame, summary,
} from "./ArticleRunner/engine";
import useLocalStorage, { readStored, writeStored } from "./Labs/useLocalStorage";
import useTheme, { THEME_LABELS, THEMES } from "./Labs/useTheme";

// Der/die/das as an endless runner. Three lanes, one per article; the noun is
// shown at the top ("___ Pferd"), a gate comes down the track, and you have to
// be in the right lane when it reaches you. Right answers speed the run up, wrong ones cost a
// life and show the answer.
//
// The world is drawn on a canvas every frame from a mutable ref, so the
// animation never goes through React. React only renders the HUD and the
// overlays, and only re-renders when a gate is resolved or spawned.
//
// Anyone can play. Saving a score to the daily leaderboard needs an account; an
// anonymous score is held in localStorage and posted once they've signed in.


const DECK_SIZES = [
  { label: "Top 100", size: 100 },
  { label: "Top 500", size: 500 },
  { label: "All", size: Infinity },
];

function playSound(url, enabled) {
  if (!url || !enabled) return;

  try {
    const audio = new Audio(url);
    const played = audio.play();
    if (played && played.catch) played.catch(() => {});
  } catch (e) {
    // No audio is fine - the game is entirely visual.
  }
}

function utcDay() {
  return new Date().toISOString().slice(0, 10);
}

function csrfToken() {
  const token = document.querySelector("meta[name=\"csrf-token\"]");
  return token ? token.getAttribute("content") : "";
}

export default function ArticleRunner({
  articles,
  audioBaseUrl,
  challenge,
  correctAudioUrl,
  gameSettingsUrl,
  incorrectAudioUrl,
  isSignedIn,
  leaderboardUrl,
  nouns,
  practiceUrl,
  quickCapture,
  scoresUrl,
  shareUrl,
  signInUrl,
  signUpUrl,
  storagePrefix,
  title,
}) {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const wrapRef = useRef(null);
  const gameRef = useRef(null);
  const viewRef = useRef(null);
  const pointerRef = useRef(null);

  const [phase, setPhase] = useState("start");
  const [hud, setHud] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [toast, setToast] = useState(null);
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState(null);
  const [saveState, setSaveState] = useState(null);
  const [best, setBest] = useLocalStorage(storagePrefix, "best", 0);
  // Two switches, not one: the words are the lesson and the beeps are not, so
  // someone playing on mute in public may still want one and not the other.
  const [soundEffects, setSoundEffects] = useLocalStorage(storagePrefix, "soundEffects", true);
  const [wordAudio, setWordAudio] = useLocalStorage(storagePrefix, "wordAudio", true);
  // Off for players who'd rather not lean on the English while they run. The
  // crash card and the game over list still say what a word means.
  const [translations, setTranslations] = useLocalStorage(storagePrefix, "translations", true);
  const { chooseTheme, theme } = useTheme({ gameSettingsUrl, isSignedIn, prefix: storagePrefix });
  const [deckSize, setDeckSize] = useLocalStorage(storagePrefix, "deckSize", 100);

  // Read from inside the animation loop, which doesn't re-render.
  const audioRef = useRef({ soundEffects, wordAudio });
  audioRef.current = { soundEffects, wordAudio };

  // --- Leaderboard --------------------------------------------------------

  const postScore = useCallback(
    (run) =>
      fetch(scoresUrl, {
        body: JSON.stringify({
          bestStreak: run.bestStreak,
          correct: run.correct,
          day: run.day,
          score: run.score,
          seconds: run.seconds,
        }),
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        method: "POST",
      }).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      }),
    [scoresUrl]
  );

  // Today's board, and - for someone who just signed up from the game over
  // screen - the score they were holding.
  useEffect(() => {
    const pending = readStored(storagePrefix, "pendingScore", null);
    const loadBoard = () =>
      fetch(leaderboardUrl, { credentials: "same-origin" })
        .then((response) => response.json())
        .then(setBoard)
        .catch(() => {});

    if (pending && pending.day !== utcDay()) writeStored(storagePrefix, "pendingScore", null);

    if (isSignedIn && pending && pending.day === utcDay()) {
      postScore(pending)
        .then((json) => {
          writeStored(storagePrefix, "pendingScore", null);
          setBoard(json);
        })
        .catch(loadBoard);
    } else {
      loadBoard();
    }
  }, [isSignedIn, leaderboardUrl, postScore]);

  const saveRun = useCallback(
    (run) => {
      if (run.score <= 0) return;

      if (!isSignedIn) {
        // Only the day's best is worth holding - it's the one that would count.
        const pending = readStored(storagePrefix, "pendingScore", null);
        if (!pending || pending.day !== run.day || pending.score < run.score) {
          writeStored(storagePrefix, "pendingScore", run);
        }
        return;
      }

      setSaveState("saving");
      postScore(run)
        .then((json) => {
          setBoard(json);
          setSaveState("saved");
        })
        .catch(() => setSaveState("error"));
    },
    [isSignedIn, postScore]
  );

  // --- The game -----------------------------------------------------------

  const syncHud = useCallback(() => setHud(hudState(gameRef.current)), []);

  const finish = useCallback(() => {
    const game = gameRef.current;
    const run = { ...summary(game), day: utcDay() };

    setResult(run);
    setBest((previous) => Math.max(previous || 0, game.score));
    setSaveState(null);
    saveRun(run);
    setPhase("over");
  }, [saveRun, setBest]);

  // The engine moves the game; this turns what happened into sound, sparks and
  // the HUD.
  const handleEvents = useCallback(
    (events) => {
      const game = gameRef.current;

      events.forEach((event) => {
        if (event.type === "resolved") {
          const nounAudio = audioUrl(audioBaseUrl, event.gate.noun.written);
          if (event.correct) {
            burst(game, viewRef.current, event.answer, LANE_COLORS[event.gate.noun.article]);
            playSound(correctAudioUrl, audioRef.current.soundEffects);
            window.setTimeout(() => playSound(nounAudio, audioRef.current.wordAudio), 120);
            setToast({ bonus: event.bonus, id: game.time, points: event.points });
          } else {
            playSound(incorrectAudioUrl, audioRef.current.soundEffects);
            window.setTimeout(() => playSound(nounAudio, audioRef.current.wordAudio), 350);
            setFeedback({ chosen: event.chosen, noun: event.gate.noun });
          }
        } else if (event.type === "crashOver") {
          setFeedback(null);
        } else if (event.type === "over") {
          finish();
        }
      });

      if (events.length) syncHud();
    },
    [audioBaseUrl, correctAudioUrl, finish, incorrectAudioUrl, syncHud]
  );

  const step = useCallback((dt) => handleEvents(stepGame(gameRef.current, dt)), [handleEvents]);

  const draw = useCallback((dt) => {
    const canvas = canvasRef.current;
    const view = viewRef.current;
    const game = gameRef.current;
    if (!canvas || !view) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = false;

    // The same shake on both canvases, so the articles stay on their panels.
    const amount = game.shake * 8;
    const shakeX = game.shake > 0 ? Math.round((Math.random() - 0.5) * amount) : 0;
    const shakeY = game.shake > 0 ? Math.round((Math.random() - 0.5) * amount) : 0;
    ctx.translate(shakeX, shakeY);

    drawBackdrop(ctx, view, game);
    // Far to near, so a nearer gate covers a farther one.
    game.gates
      .slice()
      .sort((a, b) => b.z - a.z)
      .forEach((gate) => drawGate(ctx, view, gate));
    drawSpeedLines(ctx, view, game);
    drawRunner(ctx, view, game);
    drawParticles(ctx, game, dt);

    // der, die and das on the full-resolution canvas on top - see drawArticles.
    const overlay = overlayRef.current;
    const textCtx = overlay && overlay.getContext("2d");
    if (!textCtx) return;
    textCtx.setTransform(1, 0, 0, 1, 0, 0);
    textCtx.clearRect(0, 0, overlay.width, overlay.height);
    textCtx.setTransform(overlay.width / view.width, 0, 0, overlay.height / view.height, 0, 0);
    textCtx.translate(shakeX, shakeY);
    drawArticles(textCtx, view, game);
  }, []);

  // Size the canvas to a fraction of its box - see makeView - and keep it there.
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;

    const resize = () => {
      const { width, height } = wrap.getBoundingClientRect();
      const view = makeView(width, height, articles);
      canvas.width = view.width;
      canvas.height = view.height;
      // The text overlay is at full device resolution.
      const dpr = window.devicePixelRatio || 1;
      overlay.width = Math.max(1, Math.round(width * dpr));
      overlay.height = Math.max(1, Math.round(height * dpr));
      viewRef.current = view;
    };

    resize();
    if (!window.ResizeObserver) {
      window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }

    const observer = new window.ResizeObserver(resize);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  // Canvas text doesn't wait for web fonts, so ask for joystix up front rather
  // than drawing the first gates in the fallback.
  useEffect(() => {
    try {
      if (document.fonts && document.fonts.load) document.fonts.load(`12px ${PIXEL_FONT}`);
    } catch (e) {
      // The fallback font is fine.
    }
  }, []);

  // One loop for the life of the page. Before the first run it draws an idle
  // track behind the start screen.
  useEffect(() => {
    if (!gameRef.current) gameRef.current = idleGame(articles);

    let frame;
    let last = performance.now();
    const tick = (now) => {
      // Clamped so a backgrounded tab doesn't come back to a gate already past.
      // And floored at 0: requestAnimationFrame's timestamp is when the frame
      // began, which can be earlier than the performance.now() taken above, and a
      // negative dt runs the clock backwards.
      const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
      last = now;
      step(dt);
      draw(dt);
      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [draw, step]);

  const start = useCallback(() => {
    const deck = nouns
      .slice()
      .sort((a, b) => a.rank - b.rank)
      .slice(0, deckSize === null ? nouns.length : deckSize);

    gameRef.current = newGame(deck, articles);
    spawnGate(gameRef.current);
    setFeedback(null);
    setToast(null);
    setResult(null);
    syncHud();
    setPhase("running");
  }, [articles, deckSize, nouns, syncHud]);

  const moveTo = useCallback(
    (lane) => {
      const game = gameRef.current;
      if (phase !== "running" || !game) return;

      if (moveRunner(game, lane) && viewRef.current) dust(game, viewRef.current);
    },
    [phase]
  );

  const dash = useCallback(() => {
    const game = gameRef.current;
    if (phase !== "running" || !game) return;

    if (startDash(game) && viewRef.current) dust(game, viewRef.current);
  }, [phase]);

  useEffect(() => {
    const onKey = (event) => {
      const game = gameRef.current;

      if (phase !== "running") {
        if (event.key === "Enter" && event.target === document.body) start();
        return;
      }

      // 1/2/3 (or j/k/l) jump straight to a lane; a key past the last lane does nothing.
      const lanes = { 1: 0, 2: 1, 3: 2, j: 0, k: 1, l: 2 };
      if (event.key === "ArrowUp" || event.key === "w" || event.key === " ") {
        dash();
      } else if (event.key === "ArrowLeft" || event.key === "a") {
        moveTo(game.lane - 1);
      } else if (event.key === "ArrowRight" || event.key === "d") {
        moveTo(game.lane + 1);
      } else if (event.key in lanes && lanes[event.key] < articles.length) {
        moveTo(lanes[event.key]);
      } else {
        return;
      }
      event.preventDefault();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dash, moveTo, phase, start]);

  // A swipe moves one lane, like the game this is borrowed from. A tap jumps
  // straight to the lane you tapped, which is faster for a quiz.
  const onPointerDown = (event) => {
    pointerRef.current = { x: event.clientX, y: event.clientY };
  };

  const onPointerUp = (event) => {
    const down = pointerRef.current;
    pointerRef.current = null;
    if (!down || phase !== "running") return;

    const dx = event.clientX - down.x;
    const dy = event.clientY - down.y;
    if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) {
      moveTo(gameRef.current.lane + Math.sign(dx));
      return;
    }
    // Swipe up to dash.
    if (dy < -30 && Math.abs(dy) > Math.abs(dx)) {
      dash();
      return;
    }

    const rect = wrapRef.current.getBoundingClientRect();
    const view = viewRef.current;
    const offset = ((event.clientX - rect.left) / view.pixel - view.width / 2) / view.laneWidth;
    moveTo(Math.round(offset + view.middle));
  };

  // Long compounds would overflow the prompt at full size.
  const promptSize = (word) => `${Math.min(32, Math.floor(480 / (word.length + 4)))}px`;

  return (
    <div className="gar">
      <div className="gar-stage" onPointerDown={onPointerDown} onPointerUp={onPointerUp} ref={wrapRef}>
        <canvas className="gar-canvas" ref={canvasRef} />
        <canvas aria-hidden="true" className="gar-canvas gar-canvas-text" ref={overlayRef} />

        {phase === "running" && hud && (
          <div className="gar-hud">
            <div className="gar-hud-row">
              <span className="gar-score">{hud.score}</span>
              {hud.streak >= 3 && (
                <span className="gar-streak">
                  {hud.streak} in a row · +{pointsFor(hud.streak)}
                </span>
              )}
              <span aria-label={`${hud.lives} lives left`} className="gar-lives">
                {Array.from({ length: LIVES }, (_, i) => (
                  <span className={i < hud.lives ? "gar-heart" : "gar-heart gar-heart-lost"} key={i}>
                    ♥
                  </span>
                ))}
              </span>
            </div>
            {hud.answered && !feedback && (
              <div className="gar-prompt gar-prompt-answered" key={`answered-${hud.answered.word}`}>
                <div
                  className="gar-prompt-word"
                  style={{ color: LANE_COLORS[hud.answered.article], fontSize: promptSize(withArticle(hud.answered)) }}
                >
                  {withArticle(hud.answered)}
                </div>
                {translations && hud.answered.gloss && <div className="gar-prompt-gloss">{hud.answered.gloss}</div>}
              </div>
            )}
            {!hud.answered && hud.noun && !feedback && (
              <div className="gar-prompt" key={hud.noun.word}>
                <div className="gar-prompt-word" style={{ fontSize: promptSize(hud.noun.word) }}>
                  <span className="gar-blank">___</span> {hud.noun.word}
                </div>
                {translations && hud.noun.gloss && <div className="gar-prompt-gloss">{hud.noun.gloss}</div>}
              </div>
            )}
          </div>
        )}

        {phase === "running" && toast && (
          <div className="gar-toast" key={toast.id}>
            +{toast.points}
            {toast.bonus > 0 && <span className="gar-toast-bonus"> dash +{toast.bonus}</span>}
          </div>
        )}

        {phase === "running" && feedback && (
          <div className="gar-overlay gar-crash">
            <div className="gar-card">
              <div className="gar-crash-label">
                Not <span className="gar-crash-article">{feedback.chosen}</span>
              </div>
              <div className="gar-answer" style={{ color: LANE_COLORS[feedback.noun.article] }}>
                {withArticle(feedback.noun)}
              </div>
              {feedback.noun.gloss && <div className="gar-answer-gloss">{feedback.noun.gloss}</div>}
              {feedback.noun.rule && (
                <div className="gar-rule">
                  {feedback.noun.ruleException
                    ? `An exception: ${feedback.noun.rule}`
                    : `Tip: ${feedback.noun.rule}`}
                </div>
              )}
            </div>
          </div>
        )}

        {phase === "start" && (
          <div className="gar-overlay">
            <div className="gar-card">
              <div className="gar-kicker">Clozemaster Labs</div>
              <div className="gar-title">
                {articles.map((article) => (
                  <React.Fragment key={article}>
                    <span className="gar-title-article" style={{ color: LANE_COLORS[article] }}>
                      {article.charAt(0).toUpperCase() + article.slice(1)}
                    </span>{" "}
                  </React.Fragment>
                ))}
                <br />
                Runner
              </div>
              {challenge && <p className="gar-challenge">Someone scored {challenge}. Can you beat it?</p>}
              <p className="gar-text">
                Each lane is an article. A noun is coming down the track - be in its lane when it
                gets to you.
              </p>
              <div aria-label="Which nouns" className="gar-deck" role="group">
                {DECK_SIZES.map(({ label, size }) => (
                  // JSON can't store Infinity, so "All" is kept as null.
                  <button
                    className={
                      (deckSize === null ? Infinity : deckSize) === size
                        ? "gar-deck-btn gar-deck-btn-active"
                        : "gar-deck-btn"
                    }
                    key={label}
                    onClick={() => setDeckSize(size === Infinity ? null : size)}
                    type="button"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button className="gar-go" onClick={start} type="button">
                Run
              </button>
              <p className="gar-hint">← → or tap a lane · swipe on a phone</p>
              <p className="gar-hint">
                Sure of it? ↑ or space (swipe up) to dash to the gate for up to +10.
              </p>
              <p className="gar-hint">
                10 points a noun, +2 for each one in a row before it, up to 30.
              </p>
              {practiceUrl && (
                <p className="gar-hint">
                  No clock? <a href={practiceUrl}>Play the classic version</a>
                </p>
              )}
              {best > 0 && <p className="gar-hint">Your best: {best}</p>}
              {board && board.top.length > 0 && (
                <div className="gar-section">
                  <div className="gar-section-title">{"Today's top runners"}</div>
                  <Leaderboard board={board} compact />
                </div>
              )}
            </div>
          </div>
        )}

        {phase === "over" && result && (
          <div className="gar-overlay">
            <GameOver
              articles={articles}
              best={best || 0}
              board={board}
              isSignedIn={isSignedIn}
              onChangeDeck={() => setPhase("start")}
              onRestart={start}
              quickCapture={quickCapture}
              result={result}
              saveState={saveState}
              shareUrl={shareUrl}
              signInUrl={signInUrl}
              signUpUrl={signUpUrl}
              title={title}
            />
          </div>
        )}
      </div>

      <div className="gar-settings">
        <button
          aria-pressed={soundEffects}
          className="gar-toggle"
          onClick={() => setSoundEffects(!soundEffects)}
          type="button"
        >
          Sound effects: {soundEffects ? "on" : "off"}
        </button>
        <button
          aria-pressed={wordAudio}
          className="gar-toggle"
          onClick={() => setWordAudio(!wordAudio)}
          type="button"
        >
          Word audio: {wordAudio ? "on" : "off"}
        </button>
        <button
          aria-pressed={translations}
          className="gar-toggle"
          onClick={() => setTranslations(!translations)}
          type="button"
        >
          Translations: {translations ? "on" : "off"}
        </button>
        <span aria-label="Theme" className="gar-theme" role="group">
          {THEMES.map((choice) => (
            <button
              aria-pressed={theme === choice}
              className={theme === choice ? "gar-toggle gar-toggle-active" : "gar-toggle"}
              key={choice}
              onClick={() => chooseTheme(choice)}
              type="button"
            >
              {THEME_LABELS[choice]}
            </button>
          ))}
        </span>
      </div>
    </div>
  );
}

ArticleRunner.defaultProps = {
  articles: GERMAN_ARTICLES,
  audioBaseUrl: null,
  challenge: null,
  correctAudioUrl: null,
  gameSettingsUrl: null,
  incorrectAudioUrl: null,
  isSignedIn: false,
  practiceUrl: null,
  quickCapture: null,
  storagePrefix: "articleRunner.",
  title: "Der Die Das Runner",
};

ArticleRunner.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.string),
  audioBaseUrl: PropTypes.string,
  challenge: PropTypes.number,
  correctAudioUrl: PropTypes.string,
  gameSettingsUrl: PropTypes.string,
  incorrectAudioUrl: PropTypes.string,
  isSignedIn: PropTypes.bool,
  leaderboardUrl: PropTypes.string.isRequired,
  nouns: PropTypes.arrayOf(
    PropTypes.shape({
      article: PropTypes.string.isRequired,
      gloss: PropTypes.string,
      rank: PropTypes.number,
      rule: PropTypes.string,
      ruleException: PropTypes.bool,
      word: PropTypes.string.isRequired,
      written: PropTypes.string.isRequired,
    })
  ).isRequired,
  practiceUrl: PropTypes.string,
  quickCapture: PropTypes.object,
  scoresUrl: PropTypes.string.isRequired,
  shareUrl: PropTypes.string.isRequired,
  signInUrl: PropTypes.string.isRequired,
  signUpUrl: PropTypes.string.isRequired,
  storagePrefix: PropTypes.string,
  title: PropTypes.string,
};
